import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Plus,
  User,
  Calendar,
  Shirt,
  MapPin,
  Clock,
  X,
  Star,
  Shield,
  ArrowRightLeft,
  Users,
  Zap,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import { toast } from 'sonner';
import WeightMonitoring from '../components/WeightMonitoring';
import InjuryHistory from '../components/athlete/InjuryHistory';
import TrainingDistributionCard from '../components/athlete/TrainingDistributionCard';
import MatchExposureCard from '../components/athlete/MatchExposureCard';
import AthleteReports from '../components/athlete/AthleteReports';
import { useSport } from '../context/SportContext';

const POSITIONS_FALLBACK = ['GK', 'CB', 'RB', 'LB', 'CDM', 'CM', 'CAM', 'RM', 'LM', 'RW', 'LW', 'ST', 'CF'];
const STATUSES = ['FULLTRAIN', 'RTP', 'OUT'];

const StatusBadge = ({ status }) => {
  const statusClasses = {
    FULLTRAIN: 'status-fulltrain',
    RTP: 'status-rtp',
    OUT: 'status-out',
  };
  return (
    <span className={`px-3 py-1.5 text-sm font-bold uppercase rounded-sm ${statusClasses[status] || ''}`}>
      {status}
    </span>
  );
};

const AthleteDetailPage = () => {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const { api, hasRole, isAdmin } = useAuth();
  const { t } = useLanguage();
  const { positions: sportPositions, sportLabel } = useSport();
  const POSITIONS = sportPositions && sportPositions.length > 0 ? sportPositions : POSITIONS_FALLBACK;

  const [athlete, setAthlete] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamAssignments, setTeamAssignments] = useState([]);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Team assignment dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignType, setAssignType] = useState('PRIMARY');
  const [assignTeamId, setAssignTeamId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [showMoveConfirm, setShowMoveConfirm] = useState(false); // confirmation when athlete already has teams

  // Daily Readiness
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);

  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canEdit = hasRole(['ADMIN', 'SNC', 'PHYSIO']);
  const canDelete = isAdmin;
  const canAssignTeam = hasRole(['ADMIN', 'SNC']);

  const fetchAthlete = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/athletes/${athleteId}`);
      setAthlete(response.data);
      setEditData(response.data);
    } catch (err) {
      setError('Failed to load athlete');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [api, athleteId]);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await api.get('/teams');
      setTeams(response.data);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  }, [api]);

  const fetchAssignments = useCallback(async () => {
    try {
      const [activeRes, historyRes] = await Promise.all([
        api.get(`/athletes/${athleteId}/teams?include_history=false`),
        api.get(`/athletes/${athleteId}/teams?include_history=true`),
      ]);
      setTeamAssignments(activeRes.data);
      setAssignmentHistory(historyRes.data.filter(a => !a.is_active));
    } catch (err) {
      console.error('Failed to fetch assignments:', err);
    }
  }, [api, athleteId]);

  const fetchReadiness = useCallback(async () => {
    setReadinessLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await api.get(`/readiness/athlete/${athleteId}?date=${today}`);
      setReadiness(res.data);
    } catch (err) {
      console.error('Failed to fetch readiness:', err);
      setReadiness(null);
    } finally {
      setReadinessLoading(false);
    }
  }, [api, athleteId]);

  useEffect(() => {
    fetchAthlete();
    fetchTeams();
    fetchAssignments();
    fetchReadiness();
  }, [fetchAthlete, fetchTeams, fetchAssignments, fetchReadiness]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put(`/athletes/${athleteId}`, {
        first_name: editData.first_name,
        last_name: editData.last_name,
        birth_date: editData.birth_date,
        primary_position: editData.primary_position,
        secondary_positions: editData.secondary_positions || [],
        jersey_number: editData.jersey_number,
        status: editData.status,
        notes: editData.notes,
      });
      toast.success(t('saveSuccess'));
      setIsEditing(false);
      fetchAthlete();
    } catch (err) {
      toast.error(t('error'));
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAssignTeam = async () => {
    if (!assignTeamId) return;
    // Check if athlete already has teams — show confirmation
    const hasPrimary = teamAssignments.some(a => a.assignment_type === 'PRIMARY');
    if (hasPrimary && assignType === 'PRIMARY' && !showMoveConfirm) {
      setShowMoveConfirm(true);
      return;
    }
    setIsAssigning(true);
    try {
      await api.post('/athlete-teams', {
        athlete_id: athleteId,
        team_id: assignTeamId,
        assignment_type: assignType,
      });
      toast.success(assignType === 'PRIMARY' ? 'Primary team set' : 'Secondary team added');
      setShowAssignDialog(false);
      setShowMoveConfirm(false);
      setAssignTeamId('');
      setAssignType('PRIMARY');
      fetchAssignments();
      fetchAthlete();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
      console.error(err);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleSetPrimary = async (assignment) => {
    try {
      // Remove the current assignment and re-create as PRIMARY
      await api.delete(`/athlete-teams/${assignment.id}`);
      await api.post('/athlete-teams', {
        athlete_id: athleteId,
        team_id: assignment.team_id,
        assignment_type: 'PRIMARY',
      });
      toast.success(`${assignment.team_name} set as primary team`);
      fetchAssignments();
      fetchAthlete();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const handleAddAsSecondary = () => {
    setAssignType('SECONDARY');
    setShowMoveConfirm(false);
    handleAssignTeamDirect('SECONDARY');
  };

  const handleMoveAsPrimary = () => {
    setShowMoveConfirm(false);
    handleAssignTeamDirect('PRIMARY');
  };

  const handleAssignTeamDirect = async (type) => {
    if (!assignTeamId) return;
    setIsAssigning(true);
    try {
      await api.post('/athlete-teams', {
        athlete_id: athleteId,
        team_id: assignTeamId,
        assignment_type: type,
      });
      toast.success(type === 'PRIMARY' ? 'Primary team changed' : 'Secondary team added');
      setShowAssignDialog(false);
      setAssignTeamId('');
      setAssignType('PRIMARY');
      fetchAssignments();
      fetchAthlete();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await api.delete(`/athlete-teams/${assignmentId}`);
      toast.success(t('deleteSuccess'));
      fetchAssignments();
      fetchAthlete();
    } catch (err) {
      toast.error(t('error'));
      console.error(err);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/athletes/${athleteId}`);
      toast.success(t('deleteSuccess'));
      navigate('/staff/roster');
    } catch (err) {
      toast.error(t('error'));
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return '-';
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">{t('loading')}</div>
      </div>
    );
  }

  if (error || !athlete) {
    return (
      <div className="text-center py-12">
        <p className="text-rose-400">{error || 'Athlete not found'}</p>
        <Button
          onClick={() => navigate('/staff/roster')}
          variant="outline"
          className="mt-4 border-zinc-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Roster
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <Button
          onClick={() => navigate('/staff/roster')}
          variant="ghost"
          size="icon"
          className="text-zinc-400 hover:text-zinc-100 self-start shrink-0"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-100">
              {athlete.first_name} {athlete.last_name}
            </h1>
            <StatusBadge status={athlete.status} />
          </div>
          <p className="text-sm text-zinc-500 mt-1">
            {athlete.primary_position} • #{athlete.jersey_number || '-'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && !isEditing && (
            <Button
              onClick={() => setIsEditing(true)}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="edit-athlete-button"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              {t('edit')}
            </Button>
          )}
          {canDelete && (
            <Button
              onClick={() => setShowDeleteDialog(true)}
              variant="outline"
              className="border-rose-900/50 text-rose-400 hover:bg-rose-900/20"
              data-testid="delete-athlete-button"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <h2 className="text-lg font-semibold text-zinc-100 mb-4">
              {isEditing ? t('editAthlete') : 'Athlete Information'}
            </h2>

            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">{t('firstName')}</Label>
                    <Input
                      value={editData.first_name || ''}
                      onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="edit-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">{t('lastName')}</Label>
                    <Input
                      value={editData.last_name || ''}
                      onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="edit-last-name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">{t('birthDate')}</Label>
                    <Input
                      type="date"
                      value={editData.birth_date || ''}
                      onChange={(e) => setEditData({ ...editData, birth_date: e.target.value })}
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="edit-birth-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">{t('jerseyNumber')}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="99"
                      value={editData.jersey_number || ''}
                      onChange={(e) => setEditData({ ...editData, jersey_number: e.target.value ? parseInt(e.target.value) : null })}
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="edit-jersey-number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">{t('primaryPosition')}</Label>
                    <Select
                      value={editData.primary_position || ''}
                      onValueChange={(val) => setEditData({ ...editData, primary_position: val })}
                    >
                      <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="edit-position">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {POSITIONS.map((pos) => (
                          <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                        ))}
                        {editData.primary_position && !POSITIONS.includes(editData.primary_position) && (
                          <SelectItem key="__legacy__" value={editData.primary_position}>
                            {editData.primary_position} (legacy)
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {editData.primary_position && !POSITIONS.includes(editData.primary_position) && (
                      <p className="text-[11px] text-amber-400/80" data-testid="legacy-position-warning">
                        This position is from a different sport ({sportLabel}). Pick a new value to update.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">{t('status')}</Label>
                    <Select
                      value={editData.status || ''}
                      onValueChange={(val) => setEditData({ ...editData, status: val })}
                    >
                      <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="edit-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400">{t('notes')}</Label>
                  <Textarea
                    value={editData.notes || ''}
                    onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 min-h-[100px]"
                    data-testid="edit-notes"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                  <Button
                    onClick={() => { setIsEditing(false); setEditData(athlete); }}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300"
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
                    data-testid="save-athlete-button"
                  >
                    {isSaving ? t('loading') : t('save')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-zinc-500" />
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500 uppercase tracking-wider">{t('birthDate')}</div>
                      <div className="text-zinc-100">{athlete.birth_date} ({calculateAge(athlete.birth_date)} years)</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-zinc-500" />
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500 uppercase tracking-wider">{t('position')}</div>
                      <div className="text-zinc-100 font-mono">{athlete.primary_position}</div>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
                      <Shirt className="w-5 h-5 text-zinc-500" />
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500 uppercase tracking-wider">{t('jerseyNumber')}</div>
                      <div className="text-zinc-100 font-mono text-xl">{athlete.jersey_number || '-'}</div>
                    </div>
                  </div>
                </div>
                {athlete.notes && (
                  <div className="col-span-2 pt-4 border-t border-zinc-800">
                    <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">{t('notes')}</div>
                    <p className="text-zinc-300 text-sm">{athlete.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Team Assignments */}
        <div className="space-y-6">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6" data-testid="team-assignments-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-amber-500" />
                <h2 className="text-lg font-semibold text-zinc-100">{t('teamAssignments')}</h2>
              </div>
              {canAssignTeam && (
                <Button
                  onClick={() => { setShowAssignDialog(true); setShowMoveConfirm(false); setAssignType('PRIMARY'); setAssignTeamId(''); }}
                  size="sm"
                  className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
                  data-testid="assign-team-button"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Team
                </Button>
              )}
            </div>

            <div className="space-y-2">
              {teamAssignments.length === 0 ? (
                <p className="text-sm text-zinc-500 py-4 text-center">No team assignments</p>
              ) : (
                teamAssignments.map((assignment) => {
                  const isPrimary = assignment.assignment_type === 'PRIMARY';
                  return (
                    <div
                      key={assignment.id}
                      className={`flex items-center justify-between p-3 rounded-sm border transition-colors ${isPrimary
                        ? 'bg-amber-500/5 border-amber-500/30'
                        : 'bg-zinc-950/50 border-zinc-800'}`}
                      data-testid={`team-assignment-${assignment.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`p-1.5 rounded-sm ${isPrimary ? 'bg-amber-500/15' : 'bg-zinc-800'}`}>
                          {isPrimary ? <Star className="w-4 h-4 text-amber-500" /> : <Shield className="w-4 h-4 text-zinc-500" />}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-zinc-100 text-sm truncate">{assignment.team_name}</div>
                          <div className="text-xs text-zinc-500 flex items-center gap-1.5">
                            {isPrimary ? (
                              <span className="text-amber-500 font-semibold">Primary</span>
                            ) : (
                              <span>Secondary</span>
                            )}
                            {assignment.team_code && <span className="text-zinc-600">({assignment.team_code})</span>}
                          </div>
                        </div>
                      </div>
                      {canAssignTeam && (
                        <div className="flex items-center gap-1 shrink-0">
                          {!isPrimary && (
                            <button
                              onClick={() => handleSetPrimary(assignment)}
                              className="p-1.5 text-zinc-500 hover:text-amber-400 transition-colors"
                              title="Set as primary team"
                              data-testid={`set-primary-${assignment.id}`}
                            >
                              <Star className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => handleRemoveAssignment(assignment.id)}
                            className="p-1.5 text-zinc-500 hover:text-rose-400 transition-colors"
                            title="Remove team assignment"
                            data-testid={`remove-assignment-${assignment.id}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Assignment History */}
            {assignmentHistory.length > 0 && (
              <div className="mt-6 pt-4 border-t border-zinc-800">
                <h3 className="text-sm font-medium text-zinc-400 mb-3">{t('assignmentHistory')}</h3>
                <div className="space-y-2">
                  {assignmentHistory.map((assignment) => (
                    <div
                      key={assignment.id}
                      className="flex items-center justify-between p-2 text-sm text-zinc-500"
                    >
                      <span>{assignment.team_name} ({assignment.assignment_type})</span>
                      <span className="text-xs">
                        {assignment.start_date?.substring(0, 10)} → {assignment.end_date?.substring(0, 10) || 'Active'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Daily Readiness Section */}
      {!isEditing && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm" data-testid="athlete-readiness-section">
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" />
              <h2 className="text-lg font-semibold text-zinc-100">Daily Readiness</h2>
              {readiness && (
                <span className="text-xs text-zinc-500 ml-1">{readiness.date}</span>
              )}
            </div>
          </div>
          <div className="p-4">
            {readinessLoading ? (
              <div className="text-sm text-zinc-500 py-2">Loading readiness...</div>
            ) : !readiness ? (
              <div className="text-sm text-zinc-500 py-2">No readiness data available for today.</div>
            ) : (() => {
              const STATUS_STYLE = {
                READY: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
                CAUTION: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
                REDUCE_LOAD: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
                RECOVERY_EMPHASIS: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
                MONITOR_CLOSELY: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
                RTP_WARNING: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
              };
              const inputs = readiness.inputs || {};
              return (
                <div className="space-y-4">
                  {/* Status + Suggestion */}
                  <div className="flex items-start gap-3">
                    <Badge className={`text-xs shrink-0 ${STATUS_STYLE[readiness.status] || 'bg-zinc-700 text-zinc-400'}`} data-testid="readiness-status-badge">
                      {readiness.status.replace(/_/g, ' ')}
                    </Badge>
                    {readiness.status !== 'READY' && readiness.suggestion && (
                      <span className="text-sm text-amber-400/80 italic" data-testid="readiness-suggestion">{readiness.suggestion}</span>
                    )}
                    {readiness.status === 'READY' && (
                      <span className="text-sm text-emerald-400/80" data-testid="readiness-suggestion">{readiness.suggestion}</span>
                    )}
                  </div>

                  {/* Reasons */}
                  <div className="space-y-1" data-testid="readiness-reasons">
                    {readiness.reasons.map((reason, i) => (
                      <div key={i} className="text-sm text-zinc-400 flex items-start gap-2">
                        <span className="text-zinc-600 mt-0.5">-</span>
                        <span>{reason}</span>
                      </div>
                    ))}
                  </div>

                  {/* Key Input Metrics */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-zinc-800/50" data-testid="readiness-inputs">
                    <div className="bg-zinc-950/50 rounded-sm p-2.5">
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Wellness</div>
                      <div className={`text-lg font-bold ${
                        inputs.wellness_readiness == null ? 'text-zinc-600' :
                        inputs.wellness_readiness < 40 ? 'text-rose-400' :
                        inputs.wellness_readiness < 60 ? 'text-amber-400' : 'text-emerald-400'
                      }`} data-testid="readiness-wellness-value">
                        {inputs.wellness_readiness != null ? Math.round(inputs.wellness_readiness) : '--'}
                      </div>
                    </div>
                    <div className="bg-zinc-950/50 rounded-sm p-2.5">
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Prev RPE</div>
                      <div className={`text-lg font-bold ${
                        inputs.last_rpe == null ? 'text-zinc-600' :
                        inputs.last_rpe >= 8 ? 'text-rose-400' :
                        inputs.last_rpe >= 6 ? 'text-amber-400' : 'text-zinc-300'
                      }`} data-testid="readiness-rpe-value">
                        {inputs.last_rpe != null ? inputs.last_rpe : '--'}
                      </div>
                    </div>
                    <div className="bg-zinc-950/50 rounded-sm p-2.5">
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">7-Day Load</div>
                      <div className="text-lg font-bold text-zinc-300" data-testid="readiness-load-value">
                        {inputs.rolling_7d_load > 0 ? Math.round(inputs.rolling_7d_load) : '--'}
                      </div>
                    </div>
                    <div className="bg-zinc-950/50 rounded-sm p-2.5">
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Trend</div>
                      <div className="flex items-center gap-1" data-testid="readiness-trend-value">
                        {inputs.wellness_trend === 'declining' && <TrendingDown className="w-4 h-4 text-rose-400" />}
                        {inputs.wellness_trend === 'improving' && <TrendingUp className="w-4 h-4 text-emerald-400" />}
                        {inputs.wellness_trend === 'stable' && <Minus className="w-4 h-4 text-zinc-400" />}
                        <span className={`text-sm font-medium capitalize ${
                          inputs.wellness_trend === 'declining' ? 'text-rose-400' :
                          inputs.wellness_trend === 'improving' ? 'text-emerald-400' :
                          inputs.wellness_trend ? 'text-zinc-400' : 'text-zinc-600'
                        }`}>
                          {inputs.wellness_trend || '--'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* RTP Context */}
                  {inputs.has_rtp && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-purple-500/5 border border-purple-500/20 rounded-sm" data-testid="readiness-rtp-context">
                      <Shield className="w-4 h-4 text-purple-400 shrink-0" />
                      <span className="text-xs text-purple-300">
                        RTP Active — Phase {inputs.rtp_phase || '?'}
                        {inputs.match_proximity && <span className="text-zinc-500 ml-2">| Match nearby: {inputs.match_proximity}</span>}
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Weight Monitoring Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <WeightMonitoring 
            athleteId={athleteId} 
            teamId={athlete.teams?.[0]?.team_id}
            isAthleteSelf={false}
          />
        </div>
      </div>

      {/* Injury History Section */}
      {!isEditing && (
        <InjuryHistory athleteId={athleteId} />
      )}

      {/* Training Distribution Section */}
      {!isEditing && (
        <TrainingDistributionCard athleteId={athleteId} />
      )}

      {/* P0.5.15 — Match Exposure Section (canonical Match Minutes / Playing Time / RPE / Match-Day Load) */}
      {!isEditing && (
        <MatchExposureCard athleteId={athleteId} />
      )}

      {/* Reports — premium PDF exports */}
      {!isEditing && (
        <AthleteReports
          athleteId={athleteId}
          athleteName={athlete ? `${athlete.first_name || ''} ${athlete.last_name || ''}`.trim() : null}
        />
      )}

      {/* Assign Team Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={(open) => { setShowAssignDialog(open); if (!open) setShowMoveConfirm(false); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">{t('assignToTeam')}</DialogTitle>
            <DialogDescription className="text-zinc-500">
              {showMoveConfirm
                ? `${athlete.first_name} already has a primary team. Choose how to add the new team.`
                : 'Select a team and assignment type.'}
            </DialogDescription>
          </DialogHeader>

          {!showMoveConfirm ? (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">{t('team')}</Label>
                  <Select value={assignTeamId} onValueChange={setAssignTeamId}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="assign-team-select">
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {teams
                        .filter(team => !teamAssignments.some(a => a.team_id === team.id))
                        .map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.code} - {team.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                {teamAssignments.length === 0 && (
                  <p className="text-xs text-zinc-600">This will be assigned as the primary team.</p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowAssignDialog(false)}
                  className="border-zinc-700 text-zinc-300"
                  data-testid="cancel-assign-button"
                >
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleAssignTeam}
                  disabled={isAssigning || !assignTeamId}
                  className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
                  data-testid="confirm-assign-button"
                >
                  {isAssigning ? t('loading') : 'Assign'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-3 py-4">
                <div className="text-sm text-zinc-300">
                  Adding <span className="font-semibold text-zinc-100">{teams.find(t2 => t2.id === assignTeamId)?.name}</span> to {athlete.first_name} {athlete.last_name}
                </div>
                <div className="space-y-2">
                  <Button
                    onClick={handleAddAsSecondary}
                    disabled={isAssigning}
                    variant="outline"
                    className="w-full justify-start border-zinc-700 text-zinc-200 hover:bg-zinc-800 h-auto py-3 px-4"
                    data-testid="add-secondary-btn"
                  >
                    <Shield className="w-5 h-5 mr-3 text-zinc-400 shrink-0" />
                    <div className="text-left">
                      <div className="font-semibold">Add as secondary team</div>
                      <div className="text-xs text-zinc-500 font-normal mt-0.5">Keep current primary team unchanged</div>
                    </div>
                  </Button>
                  <Button
                    onClick={handleMoveAsPrimary}
                    disabled={isAssigning}
                    variant="outline"
                    className="w-full justify-start border-amber-500/30 text-amber-400 hover:bg-amber-500/10 h-auto py-3 px-4"
                    data-testid="move-primary-btn"
                  >
                    <ArrowRightLeft className="w-5 h-5 mr-3 text-amber-400 shrink-0" />
                    <div className="text-left">
                      <div className="font-semibold">Move as primary team</div>
                      <div className="text-xs text-zinc-500 font-normal mt-0.5">Current primary becomes secondary</div>
                    </div>
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => { setShowMoveConfirm(false); }}
                  className="border-zinc-700 text-zinc-300"
                >
                  Back
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-100">Delete Athlete</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {t('confirmDelete')} This will deactivate the athlete and all their team assignments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-rose-600 text-white hover:bg-rose-500"
              data-testid="confirm-delete-button"
            >
              {isDeleting ? t('loading') : t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AthleteDetailPage;
